import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, CanActivateFn } from '@angular/router';
import { AuthenticationService } from '@core/services/auth.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Guarda inversa para las rutas de login (`''`, `backoffice`).
 *
 * Sin esto, cualquier navegación a `/` muestra el formulario de login
 * incondicionalmente, aunque siga habiendo un `jwt_token` válido en storage
 * (p. ej. tras un redirect erróneo de `authGuard` por un error de red). El
 * usuario lo percibe como si se hubiera cerrado la sesión de golpe.
 *
 * Si hay token y es válido, se redirige directo al dashboard. Si no hay
 * token o la verificación falla, se deja pasar al login normalmente.
 */
export const guestGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): Observable<boolean> => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  // El backend redirige aquí con `access_token` tras el SSO de Microsoft;
  // ese flujo lo procesa el propio LoginComponent y no debe interrumpirse
  // por un token viejo que aún quede en storage.
  if (route.queryParamMap.has('access_token')) {
    return of(true);
  }

  const hasToken =
    !!localStorage.getItem('jwt_token') || !!sessionStorage.getItem('jwt_token');
  if (!hasToken) {
    return of(true);
  }

  return authService.check().pipe(
    map((data) => {
      if (data.ok) {
        router.navigate(['/dashboard']);
        return false;
      }
      return true;
    }),
    catchError(() => of(true)),
  );
};
